import { useEffect, useState } from 'react';
import { Button, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@staff-app/ui';
import { RefreshCw, Search } from 'lucide-react';
import { useSearchParams } from 'react-router';
import {
  INTL_ALL_STAGES,
  INTL_ALL_STAGE_LABELS,
  INTL_ROUTES,
  INTL_ROUTE_LABELS,
  type IntlAllStage,
  type IntlRoute,
} from '@staff-app/shared-types';
import { IntlAllRequestDrawer } from '../components/IntlAllRequestDrawer';
import { IntlAllRequestForm } from '../components/IntlAllRequestForm';
import { IntlAllRequestTable } from '../components/IntlAllRequestTable';
import {
  INTL_ALL_PAGE_SIZE,
  INTL_ALL_PAGE_SIZE_OPTIONS,
  useCanFileAir,
  useIntlAllRequests,
  useRefreshIntlAllRequests,
} from '../hooks/useIntlAllRequests';

const ALL = '__all__';

/**
 * Devices Order › All International — requests that may hold several device models and ship by
 * Amazon MCF, China Fulfill or Houston Fulfill, split as fulfilment decides.
 *
 * A separate tab from International Requests on purpose (Paul, 2026-10-26): that lane is single
 * model, Amazon only, and stays exactly as it is.
 */
export function IntlAllRequestsPage() {
  // `?request=<id>` — the OptiBot Slack link (Paul, 2026-10-28): search for it and open its panel.
  const [params] = useSearchParams();
  const linked = Number(params.get('request')) || null;
  const [search, setSearch] = useState(linked ? `AIR-${linked}` : '');
  const [stage, setStage] = useState<IntlAllStage | ''>('');
  const [route, setRoute] = useState<IntlRoute | ''>('');
  const [mine, setMine] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState<number>(INTL_ALL_PAGE_SIZE);
  const [selectedId, setSelectedId] = useState<number | null>(linked);
  const canFile = useCanFileAir();

  useEffect(() => {
    setPageIndex(0);
  }, [search, stage, route, mine, pageSize]);

  const { data, loading, error, refetch } = useIntlAllRequests(
    { search, stage: stage || undefined, route: route || undefined, mine },
    pageIndex * pageSize,
    pageSize,
  );
  const items = data?.intlAllRequests?.items ?? [];
  const total = data?.intlAllRequests?.total ?? 0;
  const firstShown = total === 0 ? 0 : pageIndex * pageSize + 1;
  const lastShown = Math.min(total, pageIndex * pageSize + items.length);
  // Refresh FORCES a fresh read (Paul, 2026-10-28): Stripe invoices and Shopify / Amazon tracking
  // for the requests on this page, then reloads the list — not just the database's last copy.
  const [refreshMany, { loading: syncing }] = useRefreshIntlAllRequests();
  const [syncNote, setSyncNote] = useState<{ text: string; failed: string[] } | null>(null);
  const forceRefresh = async () => {
    setSyncNote(null);
    try {
      const res = await refreshMany({ variables: { requestIds: items.map((r) => r.id) } });
      const r = res.data?.refreshIntlAllRequests;
      if (r) {
        const parts = [
          `${r.invoicesChecked} invoice${r.invoicesChecked === 1 ? '' : 's'} checked with Stripe`,
          `${r.shipmentsChecked} shipment${r.shipmentsChecked === 1 ? '' : 's'} checked for tracking`,
        ];
        setSyncNote({ text: `${parts.join(', ')}.`, failed: r.failed });
      }
    } catch (e) {
      setSyncNote({ text: `Could not reach Stripe / Shopify: ${(e as Error).message}`, failed: [] });
    }
    await refetch().catch(() => {});
  };

  // The panel reads its row from the list, so a mutation's refetch updates both at once.
  const selected = selectedId === null ? null : (items.find((r) => r.id === selectedId) ?? null);

  return (
    <div className="flex flex-col gap-6 p-6" data-testid="air-page">
      <p className="text-sm text-muted-foreground">
        {'International hardware orders with one or more devices. Support files the request; '}
        {'fulfilment routes each device to Amazon MCF, China Fulfill or Houston Fulfill; then it is '}
        {'quoted or invoiced, with freight charged per shipment.'}
      </p>

      {canFile ? <IntlAllRequestForm onFiled={(r) => setSelectedId(r.id)} /> : null}

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[280px] max-w-md flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="AIR-112, company, contact, cus_… or in_…"
            className="pl-9"
            data-testid="air-search"
          />
        </div>
        <Select value={stage || ALL} onValueChange={(v) => setStage(v === ALL ? '' : (v as IntlAllStage))}>
          <SelectTrigger className="w-[190px]" data-testid="air-filter-stage">
            <SelectValue placeholder="Stage" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{'All stages'}</SelectItem>
            {[...INTL_ALL_STAGES].reverse().map((s) => (
              <SelectItem key={s} value={s}>
                {INTL_ALL_STAGE_LABELS[s]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={route || ALL} onValueChange={(v) => setRoute(v === ALL ? '' : (v as IntlRoute))}>
          <SelectTrigger className="w-[170px]" data-testid="air-filter-route">
            <SelectValue placeholder="Route" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{'All routes'}</SelectItem>
            {INTL_ROUTES.map((r) => (
              <SelectItem key={r} value={r}>
                {INTL_ROUTE_LABELS[r]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant={mine ? 'default' : 'outline'}
          onClick={() => setMine((v) => !v)}
          aria-pressed={mine}
          data-testid="air-filter-mine"
        >
          {'Mine'}
        </Button>
        <Button
          variant="outline"
          onClick={() => void forceRefresh()}
          disabled={loading || syncing}
          title="Asks Stripe and Shopify / Amazon now for the requests on this page"
          data-testid="air-refresh"
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${loading || syncing ? 'animate-spin' : ''}`} />
          {syncing ? 'Checking Stripe and Shopify…' : 'Refresh'}
        </Button>
        <div className="ml-auto flex items-center gap-3">
          <span className="text-xs text-muted-foreground" data-testid="air-count">
            {total === 0 ? 'No requests' : `${firstShown}–${lastShown} of ${total}`}
          </span>
          <span className="text-xs text-muted-foreground">{'Rows per page'}</span>
          <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
            <SelectTrigger className="h-8 w-[72px] cursor-pointer" aria-label="Rows per page" data-testid="air-page-size">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {INTL_ALL_PAGE_SIZE_OPTIONS.map((n) => (
                <SelectItem key={n} value={String(n)} className="cursor-pointer">
                  {n}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {syncNote ? (
        <div className="text-xs text-muted-foreground" data-testid="air-refresh-note">
          <p>{syncNote.text}</p>
          {syncNote.failed.length ? (
            <ul className="mt-1 list-disc pl-5 text-destructive" data-testid="air-refresh-failed">
              {syncNote.failed.map((f) => (
                <li key={f}>{f}</li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
      {error ? (
        <p className="text-sm text-destructive" data-testid="air-list-error">
          {error.message}
        </p>
      ) : null}

      <div className="rounded-md border">
        <IntlAllRequestTable requests={items} onSelect={(r) => setSelectedId(r.id)} />
      </div>

      <IntlAllRequestDrawer request={selected} onClose={() => setSelectedId(null)} />

      {total > pageSize ? (
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={() => setPageIndex((i) => Math.max(0, i - 1))}
            disabled={pageIndex === 0 || loading}
            data-testid="air-prev"
          >
            {'Previous'}
          </Button>
          <Button
            variant="outline"
            onClick={() => setPageIndex((i) => i + 1)}
            disabled={lastShown >= total || loading}
            data-testid="air-next"
          >
            {'Next'}
          </Button>
          <span className="text-xs text-muted-foreground">
            {`Page ${pageIndex + 1} of ${Math.max(1, Math.ceil(total / pageSize))}`}
          </span>
        </div>
      ) : null}
    </div>
  );
}
